import { publicFetch, publicAssetUrl } from './publicFetch';
import { getShareSessionToken } from './shareContext';
import { SitePlanItem, UpcomingProject } from '../types';

/** Module enum mirrored from `backend/src/entities/TransformationPickerExternalUser.ts`. */
export type PublicShareModule =
  | 'PICKER_SITE_PLANS'
  | 'PICKER_PROJECT_DETAILS'
  | 'PICKER_PROJECT_RESEARCH';

/** Response shape for `GET /api/super-picker-public/share/:uuid`. */
export interface PublicShareResolveResponse {
  uuid: string;
  module: PublicShareModule;
  projectId: string | null;
  name: string | null;
  /**
   * Per-recipient rate-limit counters from
   * `TransformationPickerExternalUser`. The backend does not enforce these
   * yet (the entity comment flags them as "reserved for future
   * rate-limiting"), but the public SPA renders the remaining count in
   * `PublicHeader` as a usage hint.
   */
  remainingTokens: number;
  maxTokens: number;
  /**
   * The agent who issued this share link (resolved from `InvitedBy`'s
   * CEANumber against the `User` table). `null` when the agent could not be
   * found; individual string fields may be `null` even when the object is
   * populated. `photo` is the raw `User.Photo` path — pair it with
   * `buildAgentPhotoUrl` for an absolute URL.
   */
  invitedBy: {
    ceaNumber: string;
    name: string | null;
    email: string | null;
    mobile: string | null;
    photo: string | null;
  } | null;
  /**
   * Absolute ISO-8601 timestamp at which this share link's trial period
   * elapses. The SPA renders a live countdown and self-kicks the
   * recipient when the deadline passes; the backend independently
   * enforces the same limit (every gated endpoint refuses 410
   * `SHARE_EXPIRED` past this time). `null` before activation.
   */
  expiresAt: string | null;
  /**
   * ISO-8601 timestamp when the recipient verified their WhatsApp OTP.
   * `null` until verification — the SPA shows the OTP dialog instead of
   * the bound page in that case.
   */
  activatedAt: string | null;
  /**
   * Last-4-digit-masked recipient mobile (e.g. `"••••1234"`). Surfaced
   * so the OTP dialog can echo the destination back to the user without
   * leaking the full number to anyone who hits the resolve endpoint.
   */
  mobileMasked: string | null;
  /**
   * ISO-8601 timestamp the link was manually revoked (defuncted) by the
   * agent or an admin. `null` for live rows. When set, the SPA shows
   * the "link revoked" blocked screen with the agent contact card.
   */
  defunctAt: string | null;
}

/** Response from `POST /share/:uuid/otp/send`. */
export interface SendShareOtpResponse {
  sent: true;
  expiresAt: string;
  mobileMasked: string | null;
}

/**
 * Request a WhatsApp OTP for the recipient. The backend rate-limits to
 * one send per 60 seconds per uuid. Throws on non-2xx with the error
 * `code` attached so the SPA can render `OTP_COOLDOWN` differently from
 * `OTP_DELIVERY_FAILED`.
 */
export async function sendShareOtp(uuid: string): Promise<SendShareOtpResponse> {
  const res = await fetch(
    `/api/super-picker-public/share/${encodeURIComponent(uuid)}/otp/send`,
    { method: 'POST', credentials: 'omit' },
  );
  const payload = (await res.json().catch(() => ({}))) as Partial<SendShareOtpResponse> & {
    error?: string;
    code?: string;
    retryAfterSec?: number;
  };
  if (!res.ok) {
    const err = new Error(payload.error || `Failed to send OTP: ${res.status}`) as Error & {
      code?: string;
      retryAfterSec?: number;
    };
    err.code = payload.code;
    err.retryAfterSec = payload.retryAfterSec;
    throw err;
  }
  return {
    sent: true,
    expiresAt: String(payload.expiresAt ?? ''),
    mobileMasked: payload.mobileMasked ?? null,
  };
}

/** Response from `POST /share/:uuid/otp/verify`. */
export interface VerifyShareOtpResponse {
  activatedAt: string;
  expiresAt: string | null;
  /**
   * Per-device session JWT issued by the backend on successful
   * activation. The SPA stores this in `localStorage` (keyed by uuid)
   * and attaches it as `Authorization: Bearer` on every gated call —
   * see `publicFetch` and `getShareSessionToken`. Forwarding the URL
   * to a different device leaves them without a token; the backend's
   * `send` / `verify` routes both 409 once the share is activated, so
   * the second device cannot launder a fresh token either.
   */
  sessionToken: string;
}

/**
 * Exchange a 6-digit code for activation. On success the SPA also gets
 * back the new `expiresAt` so the trial countdown can start immediately
 * without a second resolve round-trip.
 */
export async function verifyShareOtp(
  uuid: string,
  code: string,
): Promise<VerifyShareOtpResponse> {
  const res = await fetch(
    `/api/super-picker-public/share/${encodeURIComponent(uuid)}/otp/verify`,
    {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    },
  );
  const payload = (await res.json().catch(() => ({}))) as Partial<VerifyShareOtpResponse> & {
    error?: string;
    code?: string;
  };
  if (!res.ok) {
    const err = new Error(payload.error || `Failed to verify OTP: ${res.status}`) as Error & {
      code?: string;
    };
    err.code = payload.code;
    throw err;
  }
  return {
    activatedAt: String(payload.activatedAt ?? ''),
    expiresAt: payload.expiresAt ?? null,
    sessionToken: String(payload.sessionToken ?? ''),
  };
}

/**
 * Mirror of the agent app's `getPhotoUrl` (see `super-picker/SuperPickerApp.tsx`).
 * Returns `undefined` when no photo is set so MUI `Avatar` can fall back to
 * its initials child.
 */
export function buildAgentPhotoUrl(photo: string | null | undefined): string | undefined {
  if (!photo) return undefined;
  if (/^https?:\/\//i.test(photo)) return photo;
  const trimmed = photo.startsWith('/') ? photo.slice(1) : photo;
  return `https://portal.huttonsgroup.com/${trimmed}`;
}

/**
 * Spend one token from the current share link. Returns the updated counters
 * so the caller can push them into `shareContext`. Throws `Error` (with the
 * backend `code` accessible via `err.message` where useful — e.g.
 * `NO_TOKENS_LEFT` when the recipient has none left).
 *
 * Unlike most public-API calls, this route lives under `/share/:uuid/...`
 * (the resolve-route family), so it bypasses the signature/auth middleware
 * and we hit it with a plain `fetch` like `resolveShareLink`.
 */
export async function consumeShareToken(
  uuid: string,
): Promise<{ remainingTokens: number; maxTokens: number }> {
  const res = await fetch(
    `/api/super-picker-public/share/${encodeURIComponent(uuid)}/consume`,
    { method: 'POST', credentials: 'omit' },
  );
  const payload = (await res.json().catch(() => ({}))) as {
    remainingTokens?: number;
    maxTokens?: number;
    error?: string;
    code?: string;
  };
  if (!res.ok) {
    const message =
      payload.code === 'NO_TOKENS_LEFT'
        ? 'You have no tokens left.'
        : payload.error || `Failed to consume token: ${res.status}`;
    const err = new Error(message);
    (err as Error & { code?: string }).code = payload.code;
    throw err;
  }
  return {
    remainingTokens: Number(payload.remainingTokens ?? 0),
    maxTokens: Number(payload.maxTokens ?? 0),
  };
}

/**
 * Body shape for `POST /api/super-picker-public/share/:uuid/reveal`. Each
 * `section` discriminator mirrors a server-side reveal handler.
 *
 * The dialog/hook passes one of these to `revealSection` to spend a token
 * and pull back the unmasked numbers for that section. Extend this union as
 * `towerView`, `bestByUnitType`, and `searchUnits` sections come online.
 */
export type RevealRequest =
  | { section: 'projectDetails'; projectId: string }
  /**
   * Tower-view reveal. Supply `unitKey` (matching the synthetic key on
   * `PublicTowerViewUnit`) to unmask a single row; supply `unitKeys` for a
   * bulk reveal in one request; omit both to unmask every unit for the
   * project in one shot (legacy).
   */
  | { section: 'towerView'; projectId: string; unitKey?: string; unitKeys?: string[] };

/** Unmasked indicative-price range returned by the `projectDetails` reveal. */
export interface RevealedIndicativePrices {
  br1: { low: number | null; high: number | null } | null;
  br2: { low: number | null; high: number | null } | null;
  br3: { low: number | null; high: number | null } | null;
  br4: { low: number | null; high: number | null } | null;
  br5: { low: number | null; high: number | null } | null;
  ph: { low: number | null; high: number | null } | null;
}

/**
 * Unmasked pricing for a single tower-view unit. Keyed in the backend
 * response by the same synthetic `key` (`block-unitNumber`) the masked
 * payload uses, so callers can swap masked strings for revealed numbers
 * row-by-row without leaking DB ids.
 */
export interface RevealedTowerViewUnitPrices {
  price: number | null;
  psf: number | null;
  priceNps: number | null;
  priceDps: number | null;
  psfNps: number | null;
  psfDps: number | null;
}

/**
 * Discriminated response for `POST .../share/:uuid/reveal`. Each branch
 * carries the same `remainingTokens` / `maxTokens` counters the consume
 * route returns plus the unmasked payload for the requested section.
 */
export type RevealResponse =
  | {
      section: 'projectDetails';
      projectId: string;
      indicativePrices: RevealedIndicativePrices | null;
      remainingTokens: number;
      maxTokens: number;
    }
  | {
      section: 'towerView';
      projectId: string;
      units: Record<string, RevealedTowerViewUnitPrices>;
      remainingTokens: number;
      maxTokens: number;
      /** Present on bulk `unitKeys` reveals — how many new tokens were charged. */
      tokensSpent?: number;
    };

/**
 * Spend one token and reveal the unmasked values for a given section. The
 * server returns the updated counters alongside the unmasked payload — the
 * caller should plumb the counters into `shareContext` (the
 * `TokenSpendDialog` does this automatically) and stash the unmasked data
 * in the `RevealedPrices` context so subsequent renders skip the masked
 * placeholder.
 */
export async function revealSection(
  uuid: string,
  body: RevealRequest,
): Promise<RevealResponse> {
  // Bypass `publicFetch` because the uuid is in the URL path (not the
  // query string), but we still need to attach the share-session JWT
  // by hand so the backend's `enforceShareSession` check on
  // `/share/:uuid/reveal` passes. Without this header the gate 401s
  // with `SHARE_SESSION_REQUIRED` even though the SPA has the token.
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const sessionToken = getShareSessionToken();
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
  const res = await fetch(
    `/api/super-picker-public/share/${encodeURIComponent(uuid)}/reveal`,
    {
      method: 'POST',
      credentials: 'omit',
      headers,
      body: JSON.stringify(body),
    },
  );
  const payload = (await res.json().catch(() => ({}))) as Partial<RevealResponse> & {
    error?: string;
    code?: string;
  };
  if (!res.ok) {
    const message =
      payload.code === 'NO_TOKENS_LEFT'
        ? 'You have no tokens left.'
        : payload.error || `Failed to reveal section: ${res.status}`;
    const err = new Error(message);
    (err as Error & { code?: string }).code = payload.code;
    throw err;
  }
  return payload as RevealResponse;
}

/**
 * Shape returned by `GET /api/super-picker-public/share/:uuid/reveals`.
 * Mirrors `RevealedPricesStore` on the SPA so seeding is a straight copy
 * — `towerView[projectId][syntheticKey]` maps to one revealed unit's
 * unmasked prices, and `projectDetails[projectId]` is the unmasked
 * indicative-prices block.
 */
export interface ExistingRevealsResponse {
  towerView: Record<string, Record<string, RevealedTowerViewUnitPrices>>;
  projectDetails: Record<string, RevealedIndicativePrices>;
}

/**
 * Free hydration call — returns the unmasked data for every reveal the
 * recipient has *already* paid for via `revealSection`. Used on SPA boot
 * so a refresh re-seeds `RevealedPricesContext` without spending another
 * token. Bypasses `publicFetch` because the uuid hasn't been installed
 * into `shareContext` yet at the time we call this.
 */
export async function fetchExistingReveals(
  uuid: string,
): Promise<ExistingRevealsResponse> {
  // Same shape as `revealSection`: uuid is in the path so we don't go
  // through `publicFetch`, but `enforceShareSession` still demands the
  // Bearer JWT. The SPA always calls this AFTER `setShareSessionToken`
  // has run (post-OTP success in `OtpVerificationDialog`, or hydration
  // from `localStorage` in `SuperPickerPublicApp`), so the token is in
  // memory by this point.
  const headers: Record<string, string> = {};
  const sessionToken = getShareSessionToken();
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;
  const res = await fetch(
    `/api/super-picker-public/share/${encodeURIComponent(uuid)}/reveals`,
    { credentials: 'omit', headers },
  );
  if (!res.ok) {
    throw new Error(`Failed to load existing reveals: ${res.status}`);
  }
  const payload = (await res.json().catch(() => ({}))) as Partial<ExistingRevealsResponse>;
  return {
    towerView: payload.towerView ?? {},
    projectDetails: payload.projectDetails ?? {},
  };
}

/**
 * Resolve a share `uuid` (taken from the recipient's URL) into the module
 * + project the link grants access to. The endpoint is unauthenticated; it
 * returns 404 when the row is missing / defunct. We deliberately bypass
 * `publicFetch` here — the caller hasn't installed the uuid into
 * `shareContext` yet, and the resolve route doesn't require it on the URL.
 */
export async function resolveShareLink(uuid: string): Promise<PublicShareResolveResponse> {
  const res = await fetch(
    `/api/super-picker-public/share/${encodeURIComponent(uuid)}`,
    { credentials: 'omit' },
  );
  if (!res.ok) {
    const payload = await res.json().catch(() => ({} as Record<string, unknown>));
    const message =
      typeof payload?.error === 'string' && (payload.error as string).length > 0
        ? (payload.error as string)
        : `Failed to resolve share link: ${res.status}`;
    const err = new Error(message) as Error & { code?: string };
    if (typeof payload?.code === 'string') err.code = payload.code as string;
    throw err;
  }
  return (await res.json()) as PublicShareResolveResponse;
}

/**
 * Raw shape returned by `GET /api/super-picker-public/top-viewing-site-plans`.
 * Mirrors `handleListTopViewingSitePlans` in `backend/src/routes/projects.ts`.
 */
export interface RawTopViewingSitePlan {
  projectId: string;
  projectName: string;
  coordinateCount: number;
  thumbnailUrl?: string;
  topDate?: string;
  district?: string;
  region?: string;
  area?: string;
  location?: string;
  mapLatitude?: number | null;
  mapLongitude?: number | null;
  upcoming: boolean;
  upcomingSeq: number | null;
  weeklySoldCount: number;
  weeklyRank: number | null;
}

interface TopViewingSitePlansResponse {
  sitePlans: RawTopViewingSitePlan[];
}

/**
 * Fetch the masked, EXTERNAL-only list of site plans. Throws on non-2xx.
 */
export async function fetchTopViewingSitePlans(): Promise<RawTopViewingSitePlan[]> {
  const res = await publicFetch('/api/super-picker-public/top-viewing-site-plans');
  if (!res.ok) {
    throw new Error(`Failed to load site plans: ${res.status}`);
  }
  const data = (await res.json()) as TopViewingSitePlansResponse;
  return Array.isArray(data?.sitePlans) ? data.sitePlans : [];
}

const formatDistrict = (d?: string): string => {
  if (!d) return '';
  // Backend returns the raw district number (e.g. "18" or "D18"); normalise.
  const trimmed = d.trim();
  return /^d\d/i.test(trimmed) ? trimmed.toUpperCase() : `D${trimmed}`;
};

export function toUpcomingProject(raw: RawTopViewingSitePlan): UpcomingProject {
  return {
    id: raw.projectId,
    name: raw.projectName,
    district: formatDistrict(raw.district),
    topYear: raw.topDate ?? '',
    region: raw.region ?? raw.area ?? '',
    street: raw.location ?? '',
    imageUrl: raw.thumbnailUrl ?? null,
    mapLatitude:
      raw.mapLatitude != null && Number.isFinite(Number(raw.mapLatitude))
        ? Number(raw.mapLatitude)
        : null,
    mapLongitude:
      raw.mapLongitude != null && Number.isFinite(Number(raw.mapLongitude))
        ? Number(raw.mapLongitude)
        : null,
  };
}

export function toSitePlanItem(raw: RawTopViewingSitePlan): SitePlanItem {
  return {
    id: raw.projectId,
    projectName: raw.projectName,
    district: formatDistrict(raw.district),
    topYear: raw.topDate ?? '',
    region: raw.region ?? raw.area ?? '',
    street: raw.location ?? '',
    imageUrl: raw.thumbnailUrl ?? null,
    mapLatitude:
      raw.mapLatitude != null && Number.isFinite(Number(raw.mapLatitude))
        ? Number(raw.mapLatitude)
        : null,
    mapLongitude:
      raw.mapLongitude != null && Number.isFinite(Number(raw.mapLongitude))
        ? Number(raw.mapLongitude)
        : null,
  };
}

/**
 * Site-plan payload returned by
 * `GET /api/super-picker-public/projects/:projectId/site-plan`.
 * No DB primary keys — mirrors `PublicSitePlan` in
 * `backend/src/routes/superPickerPublic.ts`.
 */
export interface PublicSitePlanBlock {
  blockNumber: string | null;
  stack: string | null;
  facing: string | null;
  /** Percentages (0-100) of the image. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PublicSitePlanPayload {
  imageUrl: string;
  blocks: PublicSitePlanBlock[];
  blockNumbers: string[];
  bounds: { x: number; y: number; width: number; height: number } | null;
}

export async function fetchPublicSitePlan(
  projectId: string,
): Promise<PublicSitePlanPayload | null> {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/site-plan`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load site plan: ${res.status}`);
  const data = (await res.json()) as { sitePlan?: PublicSitePlanPayload };
  return data?.sitePlan ?? null;
}

/**
 * Per-stack sold-percentage map (0-100) for the heat-map overlay.
 * `GET /api/super-picker-public/projects/:projectId/stack-sold-percentages`.
 */
export async function fetchPublicStackSoldPercentages(
  projectId: string,
): Promise<Record<string, number>> {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/stack-sold-percentages`,
  );
  if (!res.ok) {
    throw new Error(`Failed to load stack sold percentages: ${res.status}`);
  }
  const data = (await res.json()) as { stackSoldPercentages?: Record<string, number> };
  return data?.stackSoldPercentages ?? {};
}

/** One unit row in the public stack-analysis dialog (masked pricing). */
export interface PublicStackAnalysisUnit {
  unitKey: string;
  unitNumber: string;
  floorNumber: string | null;
  stackNumber: string | null;
  blockNumber: string | null;
  unitType: string | null;
  bedroomNumber: number | null;
  bathroomNumber: number | null;
  size: number | null;
  priceDisplay: string | null;
  psfDisplay: string | null;
  status: string | null;
  floorplan: string | null;
  projectName: string | null;
}

export interface PublicStackAnalysisPayload {
  projectName: string | null;
  stackNumber: string;
  facing: string | null;
  totalUnits: number;
  availableUnits: number;
  soldUnits: number;
  bedroomBreakdown: Record<string, { available: number; sold: number }>;
  unitTypeBreakdown: Record<string, { available: number; sold: number }>;
  units: PublicStackAnalysisUnit[];
}

export async function fetchPublicStackAnalysis(
  projectId: string,
  stackNumber: string,
  blockNumber?: string | null,
): Promise<PublicStackAnalysisPayload | null> {
  const params = new URLSearchParams();
  if (blockNumber) params.set('blockNumber', blockNumber);
  const qs = params.toString();
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/stack-analysis/${encodeURIComponent(stackNumber)}${qs ? `?${qs}` : ''}`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load stack analysis: ${res.status}`);
  const data = (await res.json()) as { stackAnalysis?: PublicStackAnalysisPayload };
  return data?.stackAnalysis ?? null;
}

/**
 * Public project-details payload (masked indicative prices, no DB ids).
 * Mirrors `PublicProjectDetails` in `backend/src/routes/superPickerPublic.ts`.
 */
export interface PublicProjectDetails {
  name: string | null;
  projectCategory: string | null;
  projectCategoryId: string | null;
  developer: string | null;
  tenure: string | null;
  totalUnits: number | null;
  completedDate: string | null;
  area: string | null;
  district: string | null;
  address: string | null;
  mapLongitude: number | null;
  mapLatitude: number | null;
  featuredImage: string | null;
  liveStatusEnabled: boolean;
  liveStatusUpdatedAt: string | null;
  indicativePrices: {
    br1: { low: string | null; high: string | null } | null;
    br2: { low: string | null; high: string | null } | null;
    br3: { low: string | null; high: string | null } | null;
    br4: { low: string | null; high: string | null } | null;
    br5: { low: string | null; high: string | null } | null;
    ph: { low: string | null; high: string | null } | null;
  } | null;
}

export async function fetchPublicProjectDetails(
  projectId: string,
): Promise<PublicProjectDetails | null> {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/details`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load project details: ${res.status}`);
  const data = (await res.json()) as { project?: PublicProjectDetails };
  return data?.project ?? null;
}

/**
 * Public tower-view unit row (masked pricing, synthetic `key`).
 * Mirrors `PublicTowerViewUnit` in `backend/src/routes/superPickerPublic.ts`.
 */
export interface PublicTowerViewUnit {
  key: string;
  projectName: string;
  region: string | null;
  unitNumber: string;
  blockNumber: string | null;
  /** Raw FloorNumber from ProjectUnit (e.g. "01", "G", "B1"). */
  floorNumber?: string | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  unitType: string | null;
  unitTypeDescription: string | null;
  unitStatus: string | null;
  floorplan: string | null;
  price: string | null;
  psf: string | null;
  priceNps: string | null;
  priceDps: string | null;
  psfNps: string | null;
  psfDps: string | null;
  distance?: number | null;
}

export interface PublicTowerViewUnitsPayload {
  units: PublicTowerViewUnit[];
  unitTypes: string[];
  showPsfInTowerView: boolean;
}

export async function fetchPublicTowerViewUnits(
  projectId: string,
): Promise<PublicTowerViewUnitsPayload> {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/tower-view-units`,
  );
  if (!res.ok) throw new Error(`Failed to load tower view units: ${res.status}`);
  return (await res.json()) as PublicTowerViewUnitsPayload;
}

/**
 * One row in the public Project Unit Type catalogue. Mirrors
 * `PublicProjectUnitType` in `backend/src/routes/superPickerPublic.ts`.
 * Intentionally has no DB `id` / `ids` — the public Unit Type filter
 * matches against `ProjectUnit.UnitTypeDescription` (or the code
 * fallback), so the public client never needs the primary key.
 */
export interface PublicProjectUnitType {
  unitType: string | null;
  unitTypes: string[];
  description: string;
  unitTypeCode: string | null;
  bedroom: number | null;
  size: number | null;
}

export interface PublicProjectUnitTypesPayload {
  projectId: string;
  unitTypes: PublicProjectUnitType[];
}

/**
 * Public counterpart to the agent's `GET /api/projects/:projectId/unit-types`.
 * Used by the Site Plan Unit Type filter so it lists every unit type the
 * developer defined for the project (including those with no live units).
 */
export async function fetchPublicProjectUnitTypes(
  projectId: string,
): Promise<PublicProjectUnitTypesPayload> {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/unit-types`,
  );
  if (!res.ok) throw new Error(`Failed to load unit types: ${res.status}`);
  return (await res.json()) as PublicProjectUnitTypesPayload;
}

/**
 * One row in the Best PX / Best PSF grouped tables. Mirrors
 * `PublicBestByUnitTypeRow` in `backend/src/routes/superPickerPublic.ts`.
 * Numeric fields (`sellingPrice`, `psf`, NPS/DPS variants) are pre-masked
 * strings (e.g. `"1,XXX,XXX"`). `null` means "not available".
 */
export interface PublicBestByUnitTypeRow {
  unitNumber: string;
  blockNumber: string;
  type: string | null;
  totalAreaSqft: number | null;
  sellingPrice: string | null;
  psf: string | null;
  sellingPriceNps: string | null;
  sellingPriceDps: string | null;
  psfNps: string | null;
  psfDps: string | null;
}

export interface PublicBestByUnitTypeBucket {
  id: number;
  description: string;
  unitType: string;
  units: PublicBestByUnitTypeRow[];
}

export interface PublicBestByUnitTypePayload {
  unitTypes: PublicBestByUnitTypeBucket[];
  isExecutiveCondo: boolean;
  showPsfInTowerView: boolean;
}

/**
 * `metric: 'price'` returns the cheapest unit per unit type;
 * `metric: 'psf'` returns the lowest-PSF unit per unit type.
 */
export async function fetchPublicBestByUnitType(
  projectId: string,
  metric: 'price' | 'psf',
): Promise<PublicBestByUnitTypePayload> {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/best-by-unit-type?metric=${metric}`,
  );
  if (!res.ok) throw new Error(`Failed to load best by ${metric}: ${res.status}`);
  return (await res.json()) as PublicBestByUnitTypePayload;
}

/**
 * Public project-media DTO returned by the factsheet / brochure endpoints.
 * Mirrors `PublicProjectMediaDto` in `backend/src/routes/superPickerPublic.ts`
 * — DB-internal columns (`mediaNumber`, `externalLink`, `updatedAt`) are
 * stripped server-side.
 */
export interface PublicProjectMedia {
  type: string | null;
  url: string | null;
  description: string | null;
  imageSmall: string | null;
  imageMedium: string | null;
  imageLarge: string | null;
}

const fetchPublicProjectMedia = async (
  projectId: string,
  kind: 'factsheet' | 'brochure',
): Promise<PublicProjectMedia | null> => {
  const res = await publicFetch(
    `/api/super-picker-public/projects/${encodeURIComponent(projectId)}/${kind}`,
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load ${kind}: ${res.status}`);
  return (await res.json()) as PublicProjectMedia;
};

export const fetchPublicFactsheet = (projectId: string) =>
  fetchPublicProjectMedia(projectId, 'factsheet');

export const fetchPublicBrochure = (projectId: string) =>
  fetchPublicProjectMedia(projectId, 'brochure');

/**
 * Build a `Content-Disposition: attachment` URL that streams the remote
 * media (`sourceUrl`) through the public download proxy with a
 * sanitized filename. Used by the Factsheet / Brochure download buttons.
 */
export function buildPublicMediaDownloadUrl(sourceUrl: string, filename?: string): string {
  const params = new URLSearchParams();
  params.set('url', sourceUrl);
  if (filename && filename.trim().length > 0) {
    params.set('filename', filename.trim());
  }
  // `publicAssetUrl` appends `?uuid=` (if present) so a share-link recipient
  // can use the URL directly as an `href` / `src` without hitting the gate.
  return publicAssetUrl(`/api/super-picker-public/download/media?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Public amenities — mirrors `PublicAmenitiesResponse` in
// `backend/src/routes/superPickerPublic.ts`.
// ---------------------------------------------------------------------------

export interface PublicAmenityCommon {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  /** Distance in meters from the project. */
  distance: number;
}
export interface PublicTrainStation extends PublicAmenityCommon {
  chineseName: string | null;
  /** Station code for this row's line, e.g. `DT6`. */
  code: string;
  /** Line id, e.g. `DT`. */
  mrtLine: string;
  /** Official line colour, e.g. `#3860bf`. */
  mrtColorCode: string | null;
  /** `MRT` or `LRT`. */
  stationType: string;
  /** False for stations announced but not yet operating. */
  isOpen: boolean;
}
export interface PublicSchool extends PublicAmenityCommon {
  type: string | null;
  category: string | null;
  address: string | null;
  postcode: string | null;
}
export interface PublicPreschool extends PublicAmenityCommon {
  type: string | null;
  category: string | null;
  address: string | null;
  postcode: string | null;
}
export interface PublicSupermarket extends PublicAmenityCommon {
  address: string | null;
}

export interface PublicAmenitiesPayload {
  project: {
    name: string | null;
    latitude: number;
    longitude: number;
    /** Land-lot rings of [longitude, latitude] — the site outline. Null when OneMap has no lot. */
    boundary?: Array<Array<[number, number]>> | null;
    lotNumber?: string | null;
    /**
     * Outline of the area the amenities were filtered against: the site grown
     * outwards by the search radius. Drawn instead of a circle round the pin.
     */
    searchArea?: Array<[number, number]> | null;
  };
  radius: number;
  amenities: {
    mrtLrt: PublicTrainStation[];
    schools: PublicSchool[];
    preschools: PublicPreschool[];
    supermarkets: PublicSupermarket[];
  };
}

export async function fetchPublicAmenities(
  projectId: string,
  radiusMeters: number,
): Promise<PublicAmenitiesPayload> {
  const params = new URLSearchParams({
    projectId,
    radius: String(radiusMeters),
  });
  const res = await publicFetch(`/api/super-picker-public/amenities?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to load amenities: ${res.status}`);
  return (await res.json()) as PublicAmenitiesPayload;
}

/** Public OneMap land-lot response (server-side proxy). */
export interface PublicOnemapLandLotResponse {
  lotNumber: string | null;
  ownership: string | null;
  geojson: { type: string; coordinates: unknown } | null;
}

export async function fetchPublicOnemapLandLot(
  latitude: number,
  longitude: number,
): Promise<PublicOnemapLandLotResponse> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
  });
  const res = await publicFetch(
    `/api/super-picker-public/onemap-land-lot?${params.toString()}`,
  );
  const payload = (await res.json().catch(() => ({}))) as Partial<PublicOnemapLandLotResponse> & {
    error?: string;
  };
  if (!res.ok) {
    throw new Error(payload?.error || `Failed to load land lot: ${res.status}`);
  }
  return {
    lotNumber: payload.lotNumber ?? null,
    ownership: payload.ownership ?? null,
    geojson: payload.geojson ?? null,
  };
}

// ---------------------------------------------------------------------------
// Public Project Research — `/api/super-picker-public/search-projects` and
// `/api/super-picker-public/search-units` wrappers. Mirror the agent-side
// `searchProjects` / `searchUnits` API surface but never send the
// `loanToValue`, `tenureYears`, `interestRate` params (the server ignores
// them and never returns DPS / outlay / installment columns).
// ---------------------------------------------------------------------------

export interface PublicResearchFilters {
  priceMin?: string;
  priceMax?: string;
  psfMin?: string;
  psfMax?: string;
  sizeMin?: string;
  sizeMax?: string;
  bedroomMin?: string;
  bedroomMax?: string;
  floorMin?: string;
  floorMax?: string;
  bathMin?: string;
  bathMax?: string;
  districts?: string[];
  regions?: string[];
  tenure?: string[];
  projectCategories?: string[];
  topYearMin?: string;
  topYearMax?: string;
}

export interface PublicResearchMatchedByBedroom {
  br1: number;
  br2: number;
  br3: number;
  br4: number;
  br5: number;
  ph: number;
}

export interface PublicResearchIndicativePriceRange {
  low: number | null;
  high: number | null;
}

export interface PublicResearchBrStat {
  availableUnits: number;
  /**
   * Pre-masked dollar amount from the backend (e.g. `"1,XXX,XXX"`). `null`
   * when no available unit had a price. The frontend renders this as-is.
   */
  fromPrice: string | null;
  fromPsf: string | null;
}

export interface PublicResearchProject {
  id: string;
  name: string | null;
  projectCategory: string | null;
  projectCategoryId: string | null;
  developer: string | null;
  tenure: string | null;
  totalUnits: number | null;
  soldUnits: number | null;
  completedDate: string | null;
  topDate: number | string | null;
  area: string | null;
  district: string | null;
  districtLocation: string | null;
  address: string | null;
  mapLatitude: number | null;
  mapLongitude: number | null;
  nearestMrt: string | null;
  walkingTimeToMrt: string | number | null;
  matchedUnitCount: number;
  matchedByBedroom: PublicResearchMatchedByBedroom;
  /**
   * Cheapest matched unit's price per bedroom bucket, already masked by the
   * backend (e.g. `"1,XXX,XXX"`). `null` when no matched unit had a price.
   * Powers the optional "1BR fr." / "2BR fr." / ... columns in the public
   * Project Research results table.
   */
  matchedLowestPriceByBedroom: {
    br1: string | null;
    br2: string | null;
    br3: string | null;
    br4: string | null;
    br5: string | null;
    ph: string | null;
  };
  brStats: {
    br1: PublicResearchBrStat | null;
    br2: PublicResearchBrStat | null;
    br3: PublicResearchBrStat | null;
    br4: PublicResearchBrStat | null;
    br5: PublicResearchBrStat | null;
    ph: PublicResearchBrStat | null;
  } | null;
  indicativePrices: {
    br1: PublicResearchIndicativePriceRange | null;
    br2: PublicResearchIndicativePriceRange | null;
    br3: PublicResearchIndicativePriceRange | null;
    br4: PublicResearchIndicativePriceRange | null;
    br5: PublicResearchIndicativePriceRange | null;
    ph: PublicResearchIndicativePriceRange | null;
  } | null;
}

export interface PublicResearchUnit {
  id: number;
  projectId: string | null;
  projectName: string;
  unitNumber: string;
  blockNumber: string | null;
  computedFloor: number | null;
  computedStack: number | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  /**
   * Pre-masked dollar amount from the backend (e.g. `"1,XXX,XXX"`). The
   * frontend renders this as-is and never sees the raw numeric value.
   */
  price: string | null;
  psf: string | null;
  unitTypeDescription: string | null;
  floorplan: string | null;
}

export interface PublicSearchProjectsResponse {
  projects: PublicResearchProject[];
  totalProjects: number;
  totalMatchedUnits: number;
}

export interface PublicSearchUnitsResponse {
  items: PublicResearchUnit[];
  projects: PublicResearchProject[];
  page: number;
  pageSize: number;
  totalUnits: number;
  totalPages: number;
}

const appendFilters = (params: URLSearchParams, filters: PublicResearchFilters): void => {
  if (filters.priceMin) params.set('priceMin', filters.priceMin);
  if (filters.priceMax) params.set('priceMax', filters.priceMax);
  if (filters.psfMin) params.set('psfMin', filters.psfMin);
  if (filters.psfMax) params.set('psfMax', filters.psfMax);
  if (filters.sizeMin) params.set('sizeMin', filters.sizeMin);
  if (filters.sizeMax) params.set('sizeMax', filters.sizeMax);
  if (filters.bedroomMin) params.set('bedroomMin', filters.bedroomMin);
  if (filters.bedroomMax) params.set('bedroomMax', filters.bedroomMax);
  if (filters.floorMin) params.set('floorMin', filters.floorMin);
  if (filters.floorMax) params.set('floorMax', filters.floorMax);
  if (filters.bathMin) params.set('bathMin', filters.bathMin);
  if (filters.bathMax) params.set('bathMax', filters.bathMax);
  if (filters.districts && filters.districts.length > 0) params.set('districts', filters.districts.join(','));
  if (filters.regions && filters.regions.length > 0) params.set('regions', filters.regions.join(','));
  if (filters.tenure && filters.tenure.length > 0) params.set('tenure', filters.tenure.join(','));
  if (filters.projectCategories && filters.projectCategories.length > 0) {
    params.set('projectCategories', filters.projectCategories.join(','));
  }
  if (filters.topYearMin) params.set('topYearMin', filters.topYearMin);
  if (filters.topYearMax) params.set('topYearMax', filters.topYearMax);
};

export async function searchPublicProjects(
  filters: PublicResearchFilters,
): Promise<PublicSearchProjectsResponse> {
  const params = new URLSearchParams();
  appendFilters(params, filters);
  const res = await publicFetch(`/api/super-picker-public/search-projects?${params.toString()}`);
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.error || `Failed to search projects: ${res.status}`);
  }
  const data = (await res.json()) as Partial<PublicSearchProjectsResponse>;
  return {
    projects: Array.isArray(data.projects) ? data.projects : [],
    totalProjects: Number.isFinite(Number(data.totalProjects)) ? Number(data.totalProjects) : 0,
    totalMatchedUnits: Number.isFinite(Number(data.totalMatchedUnits)) ? Number(data.totalMatchedUnits) : 0,
  };
}

export interface PublicSearchUnitsParams {
  filters: PublicResearchFilters;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** Optional scope: only return units belonging to these project names. */
  unitProjectNames?: string[];
}

export async function searchPublicUnits(
  input: PublicSearchUnitsParams,
): Promise<PublicSearchUnitsResponse> {
  const params = new URLSearchParams();
  appendFilters(params, input.filters);
  params.set('page', String(input.page));
  params.set('pageSize', String(input.pageSize));
  if (input.sortBy) params.set('sortBy', input.sortBy);
  if (input.sortDir) params.set('sortDir', input.sortDir);
  if (input.unitProjectNames && input.unitProjectNames.length > 0) {
    params.set('unitProjectNames', input.unitProjectNames.join(','));
  }
  const res = await publicFetch(`/api/super-picker-public/search-units?${params.toString()}`);
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.error || `Failed to search units: ${res.status}`);
  }
  return (await res.json()) as PublicSearchUnitsResponse;
}

export interface PublicProjectCategoryOption {
  id: string;
  name: string | null;
}

export async function fetchPublicProjectCategories(): Promise<PublicProjectCategoryOption[]> {
  const res = await publicFetch('/api/super-picker-public/project-categories');
  if (!res.ok) throw new Error(`Failed to load project categories: ${res.status}`);
  const data = (await res.json()) as { items?: PublicProjectCategoryOption[] };
  return Array.isArray(data.items) ? data.items : [];
}

export interface PublicDistrictOption {
  no: string;
  name: string | null;
  generalLocation: string | null;
}

export async function fetchPublicDistricts(): Promise<PublicDistrictOption[]> {
  const res = await publicFetch('/api/super-picker-public/districts');
  if (!res.ok) throw new Error(`Failed to load districts: ${res.status}`);
  const data = (await res.json()) as { items?: PublicDistrictOption[] };
  return Array.isArray(data.items) ? data.items : [];
}
